/**
 * DatePicker — shadcn Popover + Calendar based replacement for the native
 * <input type="date">. Displays as "21 Dec 2026" but emits / receives ISO
 * "YYYY-MM-DD" so the existing backend contract is unchanged.
 *
 * Props:
 *   value       (string)  — ISO "YYYY-MM-DD" or ""
 *   onChange(v: string)   — called with ISO string ("" when cleared)
 *   placeholder (string)  — empty-state label, default "Pick a date"
 *   className   (string)  — extra classes on the trigger button
 *   disabled    (bool)
 *   testId      (string)  — data-testid for the trigger
 *   align       ("start"|"end"|"center")
 *
 * Companion: <MonthPicker> for ISO "YYYY-MM".
 */
import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarDays, X } from "lucide-react";
import { fmtDate } from "@/lib/dates";

function isoToDate(iso) {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function dateToIso(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  className,
  disabled,
  testId,
  align = "start",
  allowClear = true,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = isoToDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={`h-9 rounded-sm font-mono-data text-left justify-between font-normal ${className || ""}`}
        >
          <span className={`inline-flex items-center gap-2 truncate ${selected ? "text-[#0a2540]" : "text-slate-400"}`}>
            <CalendarDays size={13} className="shrink-0" />
            <span className="truncate">{selected ? fmtDate(value) : placeholder}</span>
          </span>
          {allowClear && selected && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onChange(""); }}
              className="inline-flex items-center text-slate-400 hover:text-red-600 ml-2"
              aria-label="Clear date"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X size={12} />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="p-0 w-auto" data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => {
            onChange(dateToIso(d));
            setOpen(false);
          }}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// --- Month picker ("YYYY-MM") ----------------------------------------------

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function isoMonthToParts(iso) {
  const m = /^(\d{4})-(\d{2})$/.exec(iso || "");
  if (!m) {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  }
  return { y: Number(m[1]), m: Number(m[2]) - 1 };
}

export function MonthPicker({ value, onChange, className, disabled, testId, align = "start" }) {
  const [open, setOpen] = React.useState(false);
  const { y: selY, m: selM } = isoMonthToParts(value);
  const [year, setYear] = React.useState(selY);

  React.useEffect(() => {
    if (open) setYear(selY);
  }, [open, selY]);

  const label = value
    ? `${MONTH_NAMES[selM]} ${selY}`
    : "Pick a month";

  const pick = (mi) => {
    const yy = String(year);
    const mm = String(mi + 1).padStart(2, "0");
    onChange(`${yy}-${mm}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={`h-9 rounded-sm font-mono-data text-left justify-start font-normal ${className || ""}`}
        >
          <CalendarDays size={13} className="mr-2 shrink-0 text-slate-500" />
          <span className={`${value ? "text-[#0a2540]" : "text-slate-400"}`}>{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="p-3 w-64" data-testid={testId ? `${testId}-popover` : undefined}>
        <div className="flex items-center justify-between mb-2 gap-2">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setYear((y) => y - 1)} data-testid="month-prev-year" aria-label="Previous year">
            ‹
          </Button>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="flex-1 h-7 rounded-sm border border-slate-200 bg-white px-2 text-sm font-semibold font-mono-data text-[#0a2540] hover:border-[#0a2540] focus:outline-none focus:ring-1 focus:ring-[#0a2540] cursor-pointer text-center"
            data-testid="month-year-select"
            aria-label="Select year"
          >
            {Array.from({ length: 81 }, (_, i) => 1970 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setYear((y) => y + 1)} data-testid="month-next-year" aria-label="Next year">
            ›
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTH_NAMES.map((name, i) => {
            const isSelected = year === selY && i === selM;
            return (
              <Button
                key={name}
                variant={isSelected ? "default" : "outline"}
                size="sm"
                className={`h-8 rounded-sm text-xs ${isSelected ? "bg-[#0a2540] hover:bg-[#0f2f50]" : ""}`}
                onClick={() => pick(i)}
                data-testid={`month-opt-${i + 1}`}
              >
                {name}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
